import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import MapComponent from '/@/components/map';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';

/**
 * Full-screen (mobile only — desktop keeps the side-by-side map) "Ver en mapa" picker: tap a
 * field on the map, then confirm with "Seleccionar". Mirrors the Figma "Mapa" modal.
 */
function FieldSelectMapModal({
    open,
    onClose,
    fieldList,
    country,
    onSelect,
}: {
    open: boolean;
    onClose: () => void;
    fieldList: Field[];
    country?: string;
    onSelect: (field: Field) => void;
}) {
    const mapRef = useRef<{ focusMap?: () => void } | null>(null);
    const [pendingField, setPendingField] = useState<Field | undefined>(undefined);

    useEffect(() => {
        if (open) mapRef.current?.focusMap?.();
    }, [open]);

    const handleClose = () => {
        setPendingField(undefined);
        onClose();
    };

    const handleConfirm = () => {
        if (!pendingField) return;
        onSelect(pendingField);
        handleClose();
    };

    return (
        <ModalV2
            open={open}
            onClose={handleClose}
            title={_('Mapa')}
            maxWidth={false}
            sx={{
                '& .MuiContainer-root': {
                    position: 'fixed',
                    inset: 0,
                    top: 0,
                    left: 0,
                    transform: 'none',
                    maxWidth: 'none',
                    width: '100%',
                    height: '100%',
                    m: 0,
                    p: 0,
                    '& > .MuiPaper-root': {
                        height: '100%',
                        maxHeight: '100%',
                        width: '100%',
                    },
                },
            }}
            bodyProps={{
                p: 0,
                alignItems: 'stretch',
                sx: { flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' },
            }}
            actions={
                <Button fullWidth variant='contained' color='primary' disabled={!pendingField} onClick={handleConfirm}>
                    {_('Seleccionar')}
                </Button>
            }
        >
            <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                <MapComponent
                    ref={mapRef}
                    fields={fieldList}
                    country={country || 'AR'}
                    offline={false}
                    simplifyPolygons
                    onFieldClick={(clickedField) => setPendingField(clickedField)}
                    selectedField={pendingField}
                />
            </Box>
        </ModalV2>
    );
}

export default FieldSelectMapModal;
